import { FormEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInView } from '../hooks/useInView';
import './Landing.css';

function RevealSection({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const { ref, inView } = useInView();
  return (
    <section ref={ref} className={`home-reveal ${inView ? 'is-visible' : ''} ${className ?? ''}`}>
      {children}
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = (e.currentTarget.elements.namedItem('q') as HTMLInputElement)?.value?.trim();
    if (q) navigate(`/events?q=${encodeURIComponent(q)}`);
    else navigate('/events');
  };

  return (
    <div className="home">
      <section className="home-hero">
        <div className="home-hero-image" aria-hidden="true" />
        <div className="home-hero-bg" aria-hidden="true" />
        <div className="home-hero-content">
          <p className="home-eyebrow">Local Jobs New England North West</p>
          <h1 className="home-headline">
            Regional events calendar.
            <br />Local planning advantage.
          </h1>
          <p className="home-subline">
            One destination for major events across Armidale, Tamworth, Moree, Inverell, Narrabri,
            Gunnedah and surrounding communities.
          </p>
          <form className="home-search" onSubmit={handleSearch} role="search">
            <input
              name="q"
              type="search"
              placeholder="Search events…"
              className="home-search-input"
              autoComplete="off"
            />
            <button type="submit" className="home-search-btn">
              Find events
            </button>
          </form>
          <div className="home-actions">
            <Link to="/events" className="home-cta home-cta-solid">
              View events calendar
            </Link>
            <Link to="/submit" className="home-cta home-cta-secondary">
              Submit an event
            </Link>
          </div>
          <p className="home-hero-note">
            Place-based delivery aligned with the Local Jobs Program and regional workforce priorities.
          </p>
        </div>
      </section>

      <RevealSection className="home-section home-quick-links">
        <a className="home-link-card" href="https://www.localjobsnenw.com.au" target="_blank" rel="noreferrer">
          <span>Local Jobs NENW</span>
          <small>Main regional website</small>
        </a>
        <Link className="home-link-card" to="/events">
          <span>Events Calendar</span>
          <small>Find upcoming regional events</small>
        </Link>
        <Link className="home-link-card" to="/submit">
          <span>Submit Your Event</span>
          <small>Share events with the full region</small>
        </Link>
        <Link className="home-link-card" to="/about">
          <span>How It Works</span>
          <small>Program and planning context</small>
        </Link>
      </RevealSection>

      <RevealSection className="home-section home-story">
        <div className="home-copy">
          <p className="home-tag">What we do</p>
          <h2>Your place-based link between events, jobs and planning.</h2>
          <p>
            This calendar is designed as a practical regional planning tool. It helps local employers,
            councils, tourism operators and community services see what is coming and prepare early.
          </p>
          <p>
            Instead of event information being spread across social pages, flyers and disconnected
            websites, the calendar creates one shared view of upcoming demand windows.
          </p>
          <p>
            This approach helps organisations move from reactive planning to coordinated, practical action.
          </p>
          <div className="home-story-actions">
            <Link to="/events" className="home-cta home-cta-solid">
              Explore the calendar
            </Link>
            <Link to="/scheduler" className="home-cta home-cta-secondary">
              Plan your year ahead
            </Link>
          </div>
        </div>
        <div className="home-visual home-visual-a" aria-hidden="true" />
      </RevealSection>

      <RevealSection className="home-section home-split-hero">
        <div className="home-split-image" aria-hidden="true" />
        <div className="home-split-content">
          <p className="home-tag home-tag-light">Our region</p>
          <h2>Armidale to Moree, Tamworth to Narrabri.</h2>
          <p>
            Built for New England North West, this platform supports place-based decisions with local context.
          </p>
          <p>
            Use town and date filters to coordinate staffing, promotions, logistics and training with confidence.
          </p>
          <Link to="/events" className="home-cta home-cta-secondary">
            Browse by town
          </Link>
        </div>
      </RevealSection>

      <RevealSection className="home-section home-pillars">
        <article className="home-pillar-card">
          <span>01</span>
          <h3>Connect job seekers</h3>
          <p>Map event-driven job demand and support locals into employment close to home.</p>
        </article>
        <article className="home-pillar-card">
          <span>02</span>
          <h3>Support employers</h3>
          <p>Prepare recruitment, onboarding and workforce planning before busy periods hit.</p>
        </article>
        <article className="home-pillar-card">
          <span>03</span>
          <h3>Bring stakeholders together</h3>
          <p>Give councils, providers and operators one timeline to coordinate around.</p>
        </article>
      </RevealSection>

      <RevealSection className="home-section home-banner">
        <div className="home-banner-image" aria-hidden="true" />
        <div className="home-banner-overlay" aria-hidden="true" />
        <div className="home-banner-content">
          <p className="home-tag home-tag-light">Advertise your event</p>
          <h2>Get visibility with the organisations that matter.</h2>
          <p>
            Publish your event to a shared regional calendar used by industry, government and local
            services planning for demand peaks.
          </p>
          <div className="home-actions">
            <Link to="/submit" className="home-cta home-cta-solid">
              Submit your event now
            </Link>
            <Link to="/events" className="home-cta home-cta-secondary">
              View current events
            </Link>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="home-section home-program">
        <div className="home-program-inner">
          <div className="home-program-head">
            <p className="home-tag">Local Jobs Program alignment</p>
            <h2>Long-form information for stakeholders.</h2>
            <p className="home-program-lead">
              A shared regional planning signal for employers, councils, workforce providers and
              community organisations.
            </p>
          </div>
          <div className="home-program-grid">
            <article className="home-program-card">
              <span className="home-program-step">01</span>
              <h3>Why this platform exists</h3>
              <p>
                Regional events create predictable demand spikes across hospitality, tourism and
                local services, but planning is often fragmented when event information is spread
                across disconnected channels.
              </p>
              <p>
                The calendar creates a shared planning signal so organisations can prepare earlier,
                reduce pressure on teams and respond with local solutions.
              </p>
            </article>
            <article className="home-program-card">
              <span className="home-program-step">02</span>
              <h3>How it is used</h3>
              <p>
                Employers can align recruitment and rostering with expected event periods, while
                training providers and workforce services can target support where and when demand grows.
              </p>
              <p>
                Councils, visitor economy teams and community organisations also get a clearer
                cross-regional view for communications, logistics and service readiness.
              </p>
            </article>
            <article className="home-program-card">
              <span className="home-program-step">03</span>
              <h3>What to do next</h3>
              <p>
                Explore upcoming events, save the ones that affect your operations, and submit
                verified event details so the regional picture remains current and useful.
              </p>
              <p>
                The more contributions received from local stakeholders, the stronger the shared
                planning capability becomes for the whole New England North West region.
              </p>
            </article>
          </div>
          <div className="home-program-actions">
            <Link to="/events" className="home-program-btn home-cta-solid">
              Open events calendar
            </Link>
            <a
              href="https://www.localjobsnenw.com.au/about"
              target="_blank"
              rel="noreferrer"
              className="home-program-btn home-program-btn-secondary"
            >
              Learn about the Local Jobs Program
            </a>
          </div>
        </div>
      </RevealSection>

      <footer className="home-footer">
        <div className="home-footer-inner">
          <div className="home-footer-brand">
            <p className="home-footer-title">Regional Events Calendar</p>
            <p>
              Supporting local planning across Armidale, Tamworth, Moree, Inverell, Glen Innes,
              Gunnedah, Narrabri, Tenterfield, Walcha, Uralla and Quirindi.
            </p>
          </div>
          <div className="home-footer-col">
            <h4>Explore</h4>
            <Link to="/events">Events Calendar</Link>
            <Link to="/submit">Submit Event</Link>
            <Link to="/about">About</Link>
          </div>
          <div className="home-footer-col">
            <h4>Program</h4>
            <a href="https://www.localjobsnenw.com.au" target="_blank" rel="noreferrer">Local Jobs NENW</a>
            <a href="https://www.localjobsnenw.com.au/about" target="_blank" rel="noreferrer">How We Work</a>
            <a href="https://www.dewr.gov.au/local-jobs" target="_blank" rel="noreferrer">DEWR Local Jobs</a>
          </div>
          <div className="home-footer-col">
            <h4>Contact</h4>
            <a href="https://www.localjobsnenw.com.au/contact" target="_blank" rel="noreferrer">Get in touch</a>
            <a href="https://www.localjobsnenw.com.au/team" target="_blank" rel="noreferrer">Meet the team</a>
            <span>events.localjobsnenw.com.au</span>
          </div>
        </div>
        <div className="home-footer-bottom">
          <span>Regional Events Calendar · Local Jobs New England North West</span>
        </div>
      </footer>
    </div>
  );
}
